
import React, { useState, useEffect } from 'react';
import { Question, AnswerType } from '@/types/assessment';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SimplifiedRichTextEditor } from '@/components/ui/simplified-rich-text-editor';
import { BadgeDollarSign, X, EyeOff } from 'lucide-react';

interface QuestionFormProps {
  categoryId: string;
  question?: Question | null;
  onSave: (categoryId: string, questionData: Partial<Question>) => void;
  onCancel: () => void;
  isMaturityModel?: boolean;
}

export function QuestionForm({
  categoryId,
  question,
  onSave,
  onCancel,
  isMaturityModel = false
}: QuestionFormProps) {
  const [text, setText] = useState('');
  const [explanation, setExplanation] = useState('');
  const [answerType, setAnswerType] = useState<AnswerType>('single');
  const [required, setRequired] = useState(true);
  const [premium, setPremium] = useState(false);
  const [disabled, setDisabled] = useState(false);

  // Initialize form with existing question data if editing
  useEffect(() => {
    if (question) {
      setText(question.text);
      setExplanation(question.explanation || '');
      setAnswerType(question.answerType);
      setRequired(question.required);
      setPremium(question.premium);
      setDisabled(question.disabled || false);
    } else {
      // Reset form for new question
      setText('');
      setExplanation('');
      setAnswerType(isMaturityModel ? 'maturity' : 'single');
      setRequired(true);
      setPremium(false);
      setDisabled(false);
    }
  }, [question, isMaturityModel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!text.trim()) {
      return;
    }

    const questionData: Partial<Question> = {
      text: text.trim(),
      explanation: explanation.trim() || undefined,
      answerType,
      required,
      premium,
      disabled,
      ...(question && { id: question.id })
    };

    // For new maturity model questions, create default maturity answers
    if (!question && answerType === 'maturity') {
      questionData.answers = [
        { id: crypto.randomUUID(), text: 'Initial - Ad hoc processes', value: 0 },
        { id: crypto.randomUUID(), text: 'Repeatable - Basic processes established', value: 1 },
        { id: crypto.randomUUID(), text: 'Defined - Processes documented and standardized', value: 2 },
        { id: crypto.randomUUID(), text: 'Managed - Processes measured and controlled', value: 3 },
        { id: crypto.randomUUID(), text: 'Optimizing - Continuous process improvement', value: 4 },
      ];
    } else if (!question) {
      // For new non-maturity questions, create default answers
      questionData.answers = [
        { id: crypto.randomUUID(), text: 'Yes', value: 1 },
        { id: crypto.randomUUID(), text: 'No', value: 0 },
      ];
    }

    onSave(categoryId, questionData);
  };

  const handleAnswerTypeChange = (value: string) => {
    const newType = value as AnswerType;
    setAnswerType(newType);
  };

  return (
    <Card className={`mb-6 border-l-4 border-l-primary ${disabled ? 'opacity-70' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {question ? 'Edit Question' : 'Add New Question'}
            {disabled && (
              <span className="bg-muted text-muted-foreground px-2 py-1 text-xs rounded flex items-center gap-1">
                <EyeOff className="h-3 w-3" />
                Disabled
              </span>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question-text">Question Text *</Label>
            <Input
              id="question-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter your question"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="question-explanation">Question Explanation</Label>
            <SimplifiedRichTextEditor
              value={explanation}
              onChange={setExplanation}
              placeholder="Provide additional context or explanation for this question (optional)"
            />
            <p className="text-xs text-muted-foreground">
              This explanation will be shown to users when answering the question
            </p>
          </div>

          <div className="flex gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="answer-type">Answer Type</Label>
              <Select
                value={answerType}
                onValueChange={handleAnswerTypeChange}
                disabled={isMaturityModel}
              >
                <SelectTrigger id="answer-type">
                  <SelectValue placeholder="Select answer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Choice</SelectItem>
                  <SelectItem value="multiple">Multiple Choice</SelectItem>
                  <SelectItem value="slider">Slider</SelectItem>
                  <SelectItem value="maturity">Maturity Levels</SelectItem>
                </SelectContent>
              </Select>
              {isMaturityModel && (
                <p className="text-xs text-purple-600">
                  Answer type is fixed for maturity model questions.
                </p>
              )}
            </div>

            <div className="flex items-end">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="question-required" 
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked === true)}
                />
                <Label htmlFor="question-required">Required</Label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border p-3 rounded-md bg-[#F1F0FB]/50">
            <div className="flex items-center gap-2">
              <BadgeDollarSign className="h-5 w-5 text-[#9b87f5]" />
              <div>
                <p className="text-sm font-medium">Premium Question</p>
                <p className="text-xs text-muted-foreground">Only available to logged-in users</p>
              </div>
            </div>
            <Switch 
              checked={premium} 
              onCheckedChange={setPremium}
            />
          </div>

          {question && (
            <div className="flex items-center justify-between border p-3 rounded-md bg-muted/30">
              <div className="flex items-center gap-2">
                <EyeOff className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Disable Question</p>
                  <p className="text-xs text-muted-foreground">Hide from new assessments while preserving data</p>
                </div>
              </div>
              <Switch 
                checked={disabled} 
                onCheckedChange={setDisabled}
              />
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!text.trim()}>
              {question ? 'Update Question' : 'Add Question'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
